import styled from "styled-components";
import { Input } from "components/Form";
import { SelectStyles } from "components/Form/Form.styles";

export const PersonalDetailsContainer = styled.div`
  font-family: "Geist", sans-serif;

  /* Scoped form-control styles for signup pages only */
  & .form-control {
    margin-bottom: 0.75rem;
  }

  .row {
    margin-bottom: 20px;

    @media (max-width: 768px) {
      margin-bottom: 16px;
    }
  }

  .form-error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 0;
    line-height: 1.4;
  }

  .input-form {
    margin-bottom: 0;

    .input-form-field {
      font-size: 14px;
    }

    .input-form-label {
      color: #374151;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
      display: block;
    }

    .calendar-icon {
      top: 12px;
      right: 12px;
    }
  }

  // Custom styles for react-datepicker within signup context
  .react-datepicker-wrapper {
    width: 100%;

    .react-datepicker__input-container {
      input {
        width: 100%;
        padding: 12px 40px 12px 12px;
        border: 1px solid #d5d7da;
        border-radius: 8px;
        font-family: "Geist", sans-serif;
        font-size: 14px;
        background: #fff;
        box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
        margin-bottom: 0; /* Override any global form-control margin */
        height: 44px;

        &:focus {
          border-color: #0a7eff;
          box-shadow: 0 0 0 3px rgba(10, 126, 255, 0.1);
          outline: none;
        }

        &::placeholder {
          color: #9ca3af;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          height: 48px;
          padding: 14px 40px 14px 12px;
          font-size: 16px; /* Prevent zoom on iOS */
        }
      }
    }
  }
`;

export const FormSection = styled.div`
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

export const StyledInput = styled(Input)`
  .form-control {
    display: flex;
    padding: 12px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 8px;
    border: 1px solid #d5d7da;
    background: #fff;
    box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
    font-family: "Geist", sans-serif;
    font-size: 14px;
    height: 44px;
    margin-bottom: 0; /* Prevent double margin from parent container */

    &:focus {
      border-color: #0a7eff;
      box-shadow: 0 0 0 3px rgba(10, 126, 255, 0.1);
      outline: none;
    }

    &::placeholder {
      color: #9ca3af;
      font-size: 14px;
    }

    @media (max-width: 768px) {
      height: 48px;
      padding: 14px 12px;
      font-size: 16px; /* Prevent zoom on iOS */
    }
  }

  .form-error {
    margin-top: 4px;
    font-size: 12px;

    @media (max-width: 768px) {
      font-size: 13px;
    }
  }
`;

// Custom Select Styles for Personal Details
export const PersonalDetailsSelectStyle = (theme) => ({
  ...SelectStyles(theme),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#fff",
    border: `1px solid ${state.isFocused ? "#0a7eff" : "#d5d7da"}`,
    borderRadius: "8px",
    padding: "4px 8px",
    minHeight: "44px",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(10, 126, 255, 0.1)"
      : "0 1px 2px 0 rgba(10, 13, 18, 0.05)",
    "&:hover": {
      borderColor: state.isFocused ? "#0a7eff" : "#9ca3af",
    },
    "@media (max-width: 768px)": {
      minHeight: "48px",
      padding: "6px 8px",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 8px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#374151",
    fontFamily: '"Giest", sans-serif',
    fontSize: "14px",
    "@media (max-width: 768px)": {
      fontSize: "16px", // Prevent zoom on iOS
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontFamily: '"Giest", sans-serif',
    fontSize: "14px",
    "@media (max-width: 768px)": {
      fontSize: "16px", // Prevent zoom on iOS
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#374151",
    fontFamily: '"Giest", sans-serif',
    fontSize: "14px",
    "@media (max-width: 768px)": {
      fontSize: "16px", // Prevent zoom on iOS
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#0a7eff"
      : state.isFocused
      ? "#f3f4f6"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    fontFamily: '"Giest", sans-serif',
    fontSize: "14px",
    padding: "8px 12px",
    minHeight: "44px", // Better touch target
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: state.isSelected ? "#0a7eff" : "#f3f4f6",
    },
    "@media (max-width: 768px)": {
      fontSize: "16px",
      minHeight: "48px",
      padding: "12px",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: () => ({
    display: "none",
  }),
});

export const FormRow = styled.div`
  margin-bottom: 12px;

  @media (max-width: 768px) {
    margin-bottom: 10px;

    .col-xs-12,
    .col-sm-6,
    .col-md-3,
    .col-md-4,
    .col-md-6,
    .col-md-12 {
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  @media (max-width: 576px) {
    .col-sm-6 {
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

// Alternative: Dedicated signup form control component
export const SignupFormControl = styled(StyledInput)`
  margin-bottom: 0.75rem;

  .form-control {
    /* Signup-specific input styles */
    transition: all 0.2s ease-in-out;

    &:hover {
      border-color: #9ca3af;
    }
  }
`;
export const Divider = styled.div`
  border-bottom: 1px solid #e9eaeb;
  margin: 32px 0;
`;
